import React, {
  useEffect,
  useLayoutEffect,
  useContext,
  useState,
  useRef,
} from 'react'
import { getApi } from '../../util/api'
import useTranslation from '../shared/TranslationProvider'

const DataContext = React.createContext()

export const useData = () => {
  return useContext(DataContext)
}

export default function DataProvider({
  activeStep,
  fetchOnLoad,
  statoCustomContent,
  time_zones,
  /** When true (OP 5-step wizard only), sync step with History API like visitor login/register. */
  syncBrowserHistory = false,
  children,
}) {
  const interestKeys = [
    'new_job',
    'career_change',
    'contract_job',
    'working_for_self',
    'retirement',
  ]
  let seniorityLevels = [
    ['early_career', 'Early Career Professional'],
    ['experienced', 'Experienced Professional'],
    ['manager', 'Manager'],
    ['director', 'Director'],
    ['vp', 'VP'],
    ['executive', 'C-Suite / Executive'],
  ]
  let statoSeniorityLevels = [
    ['aide_to_helper', 'Auxiliar ou Ajudante'],
    ['assistant_to_analyst', 'Assistente ou Analista'],
    ['specialist_to_superivisor', 'Especialista, Supervisor ou Coordenador'],
    ['entry_to_mid', 'Gerente'],
    ['manager_to_director', 'Diretor'],
    ['vp_to_c_level', 'VP ou C-level'],
  ]

  if (statoCustomContent) {
    seniorityLevels = statoSeniorityLevels
  }
  const lastRoleTypeValues = [
    ['individual_contributor', 'Employee or Individual Contributor'],
    ['manager', 'Manager'],
  ]
  const seekingEducationValues = ['yes', 'no', 'not_sure']

  const { t } = useTranslation()
  const timelineValues = t('timeline_options')

  const initialStep = parseInt(activeStep, 10) || 1
  const initialStepRef = useRef(initialStep)
  const [step, _setStep] = useState(initialStep)

  // CTS-9309-style history (visitor LoggedOutExperience): replaceState + pushState + popstate so
  // the browser Back button moves between steps. In-page Back/Next still use setCurrentStep only.
  const pushOnboardingUrl = () =>
    `${window.location.pathname}${window.location.search}${window.location.hash || ''}`

  useLayoutEffect(() => {
    if (!syncBrowserHistory) return
    if (typeof window === 'undefined' || !window.history?.replaceState) return
    const url = pushOnboardingUrl()
    window.history.replaceState({ onboardingStep: 1 }, '', url)
    for (let s = 2; s <= initialStepRef.current; s++) {
      window.history.pushState({ onboardingStep: s }, '', url)
    }
  }, [syncBrowserHistory])

  useEffect(() => {
    if (!syncBrowserHistory) return undefined
    const handlePopState = (event) => {
      if (event.state && typeof event.state.onboardingStep === 'number') {
        _setStep(event.state.onboardingStep)
        window.scrollTo(0, 0)
      } else {
        _setStep(initialStepRef.current)
        window.scrollTo(0, 0)
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [syncBrowserHistory])

  const [interest, setInterest] = useState(null)
  const [candidate, setCandidate] = useState({})
  const [desiredJobTitle, setDesiredJobTitle] = useState('')
  const [desiredLocation, setDesiredLocation] = useState('')
  const [desiredIndustry, setDesiredIndustry] = useState('')
  const [aboutYourself, setAboutYourself] = useState('')
  const [linkedInProfile, setLinkedInProfile] = useState('')
  const [twitterProfile, setTwitterProfile] = useState('')
  const [facebookProfile, setFacebookProfile] = useState('')
  const [retirementGoals, setRetirementGoals] = useState('')
  const [desiredOpportunities, setDesiredOpportunities] = useState('')
  const [lastRoleType, setLastRoleType] = useState('')
  const [seniorityLevel, setSeniorityLevel] = useState('')
  const [seekingEducation, setSeekingEducation] = useState('')
  const [notificationPreference, setNotificationPreference] = useState('')
  const [resumeName, setResumeName] = useState('')
  const [resumeFileAcceptList, setResumeFileAcceptList] = useState('')
  const [serverTranslations, setServerTranslations] = useState({})
  const defaultTimezone =
    time_zones?.find(([label]) =>
      label.includes('Eastern Time (US & Canada)'),
    )?.[1] || ''
  const [candidateTimezone, setCandidateTimezone] = useState(defaultTimezone)

  const interestIsRetirement = interest === 'retirement'
  const interestIsWorkingForSelf = interest === 'working_for_self'
  const interestIsJobSeeking =
    interest != null &&
    interest !== 'retirement' &&
    interest !== 'working_for_self'

  const setCurrentStep = (nextStep) => {
    const normalizedTimezone =
      candidateTimezone === '' ? null : candidateTimezone

    getApi().patch('/onboarding/', {
      social_media_profile: {
        linked_in_profile_url: linkedInProfile,
        twitter_profile_url: twitterProfile,
        facebook_profile_url: facebookProfile,
      },
      candidate: {
        seniority_level: seniorityLevel,
        time_zone: normalizedTimezone,
        candidate_profile_attributes: {
          job_title: desiredJobTitle,
          industry: desiredIndustry,
          location: desiredLocation,
          interest: interest,
          last_role_type: lastRoleType,
          about_yourself: aboutYourself,
          retirement_goals: retirementGoals,
          desired_opportunities: desiredOpportunities,
          seeking_education: seekingEducation,
          notification_preference: notificationPreference,
        },
      },
    })
    if (nextStep > 5) {
      window.location.href = '/'
    } else {
      if (
        syncBrowserHistory &&
        typeof window !== 'undefined' &&
        window.history?.pushState
      ) {
        window.history.pushState(
          { onboardingStep: nextStep },
          '',
          pushOnboardingUrl(),
        )
      }
      _setStep(nextStep)
      window.scrollTo(0, 0)
    }
  }

  const emptyIfNull = (value) => value ?? ''

  const loadOnboardingInfo = async () => {
    const {
      data: {
        resumeFileAcceptList,
        candidate,
        candidateProfile,
        socialMediaProfile,
        translatedStrings,
      },
    } = await getApi().get('/onboarding/1')
    setCandidate(candidate)
    setResumeFileAcceptList(resumeFileAcceptList ?? '')
    if (candidateProfile) {
      setDesiredJobTitle(emptyIfNull(candidateProfile.desiredJobTitle))
      setDesiredIndustry(emptyIfNull(candidateProfile.desiredIndustry))
      setDesiredLocation(emptyIfNull(candidateProfile.desiredLocation))
      setSeniorityLevel(emptyIfNull(candidateProfile.seniorityLevel))
      setResumeName(emptyIfNull(candidateProfile.resumeName))
      setInterest(candidateProfile.interest)
      setDesiredOpportunities(
        emptyIfNull(candidateProfile.desiredOpportunities),
      )
      setLastRoleType(emptyIfNull(candidateProfile.lastRoleType))
      setAboutYourself(emptyIfNull(candidateProfile.aboutYourself))
      setRetirementGoals(emptyIfNull(candidateProfile.retirementGoals))
      setSeekingEducation(emptyIfNull(candidateProfile.seekingEducation))
      setNotificationPreference(
        emptyIfNull(candidateProfile.notificationPreference),
      )
      setCandidateTimezone(emptyIfNull(candidate.timeZone) || defaultTimezone)
    }
    if (socialMediaProfile) {
      setLinkedInProfile(socialMediaProfile.linkedInProfile || '')
      setTwitterProfile(socialMediaProfile.twitterProfile || '')
      setFacebookProfile(socialMediaProfile.facebookProfile || '')
    }
    if (translatedStrings) {
      setServerTranslations(translatedStrings)
    }
  }

  useEffect(() => {
    fetchOnLoad && loadOnboardingInfo()
  }, [fetchOnLoad])

  const data = {
    step,
    loadOnboardingInfo,
    candidate,
    interest,
    interestKeys,
    desiredJobTitle,
    desiredLocation,
    desiredIndustry,
    aboutYourself,
    retirementGoals,
    linkedInProfile,
    twitterProfile,
    facebookProfile,
    resumeName,
    resumeFileAcceptList,
    desiredOpportunities,
    lastRoleType,
    lastRoleTypeValues,
    seniorityLevel,
    seniorityLevels,
    seekingEducation,
    seekingEducationValues,
    notificationPreference,
    setInterest,
    setCurrentStep,
    setDesiredJobTitle,
    setDesiredLocation,
    setDesiredIndustry,
    setAboutYourself,
    setRetirementGoals,
    setLinkedInProfile,
    setTwitterProfile,
    setFacebookProfile,
    setResumeName,
    setDesiredOpportunities,
    setLastRoleType,
    setSeniorityLevel,
    setSeekingEducation,
    setNotificationPreference,
    getApi,
    interestIsRetirement,
    interestIsWorkingForSelf,
    interestIsJobSeeking,
    serverTranslations,
    timelineValues,
    statoCustomContent,
    candidateTimezone,
    setCandidateTimezone,
    time_zones,
  }

  return <DataContext.Provider value={data}>{children}</DataContext.Provider>
}
